import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Link,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaFileAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import {
  extractSkillsFromCv,
  getPublicMissionInfo,
  submitPublicApplication,
} from "../../api/partnersApi";
import AmbientBlobs from "../../marketing/components/AmbientBlobs";
import "../../marketing/styles.css";
import "./landing-form.css";

const LandingForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const { t, i18n } = useTranslation("public");

  // Use the marketing palette + ambient blobs while this page is mounted.
  useEffect(() => {
    document.body.classList.add("marketing-page");
    document.body.classList.add("is-landing-form");
    return () => {
      document.body.classList.remove("marketing-page");
      document.body.classList.remove("is-landing-form");
    };
  }, []);

  // Public-facing page defaults to French unless the user explicitly chose English
  useEffect(() => {
    const explicit = window.localStorage.getItem("portalLanguage");
    if (!explicit) {
      i18n.changeLanguage("fr");
    }
  }, [i18n]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mission, setMission] = useState(null);
  const [error, setError] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [skillsTechnical, setSkillsTechnical] = useState([]);
  const [skillsFunctional, setSkillsFunctional] = useState([]);
  const [skillsBusiness, setSkillsBusiness] = useState([]);
  const [cvFile, setCvFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [extractingSkills, setExtractingSkills] = useState(false);
  const [isReferral, setIsReferral] = useState(false);
  const [referrerFirstName, setReferrerFirstName] = useState("");
  const [referrerLastName, setReferrerLastName] = useState("");
  const [tacAccepted, setTacAccepted] = useState(false);

  const handleCvSelected = async (file) => {
    setCvFile(file);
    if (!file) return;
    try {
      setExtractingSkills(true);
      const result = await extractSkillsFromCv(file);
      const tech = result.skillsTechnical || [];
      const func = result.skillsFunctional || [];
      const biz = result.skillsBusiness || [];
      const total = tech.length + func.length + biz.length;
      if (total > 0) {
        setSkillsTechnical(tech);
        setSkillsFunctional(func);
        setSkillsBusiness(biz);
      }
    } catch {
      // Non-critical — skills will just be empty
    } finally {
      setExtractingSkills(false);
    }
  };

  useEffect(() => {
    const fetchMission = async () => {
      try {
        setLoading(true);
        const data = await getPublicMissionInfo(token);
        if (!data.isOpen) {
          setError(t("landing.missionClosed"));
        }
        setMission(data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError(t("landing.missionNotFound"));
        } else {
          setError(t("landing.loadFailed"));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMission();
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast({
        title: t("landing.fillRequired"),
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("firstName", firstName.trim());
      formData.append("lastName", lastName.trim());
      formData.append("email", email.trim());
      if (phone.trim()) formData.append("phone", phone.trim());

      formData.append("isReferral", isReferral ? "true" : "false");
      if (isReferral) {
        if (referrerFirstName.trim()) formData.append("referrerFirstName", referrerFirstName.trim());
        if (referrerLastName.trim()) formData.append("referrerLastName", referrerLastName.trim());
      }

      const formDataJson = JSON.stringify({
        skills_technical: skillsTechnical,
        skills_functional: skillsFunctional,
        skills_business: skillsBusiness,
        skills: [...skillsTechnical, ...skillsFunctional, ...skillsBusiness],
      });
      formData.append("formData", formDataJson);

      formData.append("tacAccepted", "true");

      if (cvFile) {
        formData.append("cv", cvFile);
      }

      await submitPublicApplication(token, formData);
      navigate(`/partners/apply/${token}/success`);
    } catch (err) {
      const detail = err.response?.data?.detail || t("landing.submissionFailed");
      toast({
        title: t("landing.error"),
        description: detail,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Header shows the client company's logo when available; otherwise empty
  // (the generic ASAP Hub mark has been removed). Attribution lives in the footer.
  const renderHeader = (logoUrl, companyName) => (
    <Box
      as="header"
      className="lf-header"
      display="flex"
      alignItems="center"
      justifyContent="center"
      pt={{ base: 6, md: 8 }}
      pb={{ base: 4, md: 6 }}
    >
      {logoUrl ? (
        <Image
          src={logoUrl}
          alt={companyName || ""}
          className="lf-header-logo"
          data-testid="landing-company-logo"
        />
      ) : null}
    </Box>
  );

  const footer = (
    <Box as="footer" className="lf-footer" data-testid="landing-footer">
      <Text as="span" className="lf-footer-text" sx={{ marginBottom: "0 !important" }}>
        {t("landing.footer.poweredBy", "Powered by")} ASAP Hub
      </Text>
    </Box>
  );

  if (loading) {
    return (
      <Box className="lf-shell" position="relative">
        <AmbientBlobs />
        {renderHeader(null)}
        <Container maxW="container.md" pb={16}>
          <Box className="lf-card lf-card-state">
            <VStack spacing={4}>
              <Spinner size="xl" color="#79f5c7" thickness="4px" />
              <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
                {t("landing.loading")}
              </Text>
            </VStack>
          </Box>
        </Container>
        {footer}
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="lf-shell" position="relative">
        <AmbientBlobs />
        {renderHeader(null)}
        <Container maxW="container.md" pb={16}>
          <Box className="lf-card lf-card-state" textAlign="center">
            <Text
              as="h1"
              fontSize="24px"
              fontWeight="900"
              color="white"
              letterSpacing="-0.03em"
              mb={2}
              sx={{ marginBottom: "8px !important" }}
            >
              {t("landing.cannotApply")}
            </Text>
            <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
              {error}
            </Text>
          </Box>
        </Container>
        {footer}
      </Box>
    );
  }

  return (
    <Box className="lf-shell" position="relative">
      <AmbientBlobs />
      {renderHeader(mission?.companyLogoUrl, mission?.companyName)}

      <Container maxW="container.md" pb={{ base: 12, md: 20 }}>
        <Box className="lf-card" data-testid="landing-form-card">
          <Box textAlign="center" mb={{ base: 6, md: 8 }}>
            <Text className="lf-kicker" sx={{ marginBottom: "8px !important" }}>
              {t("landing.applyingFor")}
            </Text>
            <Text
              as="h1"
              className="lf-title"
              sx={{ marginBottom: "0 !important" }}
            >
              {mission?.title}
            </Text>
          </Box>

          <Box as="form" onSubmit={handleSubmit} className="lf-form">
            <VStack spacing={4} align="stretch">
              <HStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel className="lf-label">{t("landing.firstName")}</FormLabel>
                  <Input
                    className="lf-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("landing.firstName")}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel className="lf-label">{t("landing.lastName")}</FormLabel>
                  <Input
                    className="lf-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("landing.lastName")}
                  />
                </FormControl>
              </HStack>

              <FormControl isRequired>
                <FormLabel className="lf-label">{t("landing.email")}</FormLabel>
                <Input
                  className="lf-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </FormControl>

              <FormControl>
                <FormLabel className="lf-label">{t("landing.phone")}</FormLabel>
                <Input
                  className="lf-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                />
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={isReferral}
                  onChange={(e) => setIsReferral(e.target.checked)}
                  className="lf-checkbox"
                >
                  <Text className="lf-checkbox-label" sx={{ marginBottom: "0 !important" }}>
                    {t("landing.referral")}
                  </Text>
                </Checkbox>
              </FormControl>

              {isReferral && (
                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel className="lf-label">{t("landing.referrerFirstName")}</FormLabel>
                    <Input
                      className="lf-input"
                      value={referrerFirstName}
                      onChange={(e) => setReferrerFirstName(e.target.value)}
                      placeholder={t("landing.referrerFirstName")}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel className="lf-label">{t("landing.referrerLastName")}</FormLabel>
                    <Input
                      className="lf-input"
                      value={referrerLastName}
                      onChange={(e) => setReferrerLastName(e.target.value)}
                      placeholder={t("landing.referrerLastName")}
                    />
                  </FormControl>
                </HStack>
              )}

              <FormControl>
                <FormLabel className="lf-label">{t("landing.cvResume")}</FormLabel>
                <Box
                  className={`lf-dropzone${dragActive ? " is-drag" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                    const file = e.dataTransfer.files?.[0];
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) {
                      toast({ title: t("landing.fileTooLarge"), status: "warning", duration: 3000 });
                      return;
                    }
                    handleCvSelected(file);
                  }}
                >
                  <VStack spacing={2}>
                    {extractingSkills ? (
                      <>
                        <Spinner size="md" color="#79f5c7" />
                        <Text className="lf-dropzone-title" sx={{ marginBottom: "0 !important" }}>
                          {t("landing.extractingSkills")}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Icon as={FaCloudUploadAlt} boxSize={6} color="#79f5c7" />
                        <Text className="lf-dropzone-title" sx={{ marginBottom: "0 !important" }}>
                          {t("landing.dragDropCv")}
                        </Text>
                        <Text className="lf-dropzone-hint" sx={{ marginBottom: "0 !important" }}>
                          {t("landing.cvFormats")}
                        </Text>
                      </>
                    )}
                  </VStack>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0] || null;
                      if (file && file.size > 10 * 1024 * 1024) {
                        toast({ title: t("landing.fileTooLarge"), status: "warning", duration: 3000 });
                        e.target.value = "";
                        setCvFile(null);
                        return;
                      }
                      handleCvSelected(file);
                    }}
                  />
                </Box>
                {cvFile && (
                  <Tag size="md" variant="subtle" className="lf-filetag" mt={2}>
                    <Icon as={FaFileAlt} mr={1} />
                    <TagLabel>{cvFile.name}</TagLabel>
                    <TagCloseButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setCvFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    />
                  </Tag>
                )}
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={tacAccepted}
                  onChange={(e) => setTacAccepted(e.target.checked)}
                  className="lf-checkbox"
                >
                  <Text className="lf-checkbox-label" sx={{ marginBottom: "0 !important" }}>
                    {t("landing.tac.label")}{" "}
                    <Link href="/terms" isExternal className="lf-tac-link">
                      {t("landing.tac.linkText")}
                    </Link>
                  </Text>
                </Checkbox>
              </FormControl>

              <button
                type="submit"
                className="mk-btn mk-btn-primary lf-submit"
                disabled={!tacAccepted || submitting}
                data-testid="landing-form-submit"
              >
                {submitting ? t("landing.submitting") : t("landing.submitNow", "Postuler maintenant")}
              </button>
            </VStack>
          </Box>
        </Box>
      </Container>
      {footer}
    </Box>
  );
};

export default LandingForm;
